import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import { REGIE_LINE_KINDS, regieExtra, type RegieExtra, type RegieZuschlag } from '@symphenia/shared-types'
import { feldKlasse } from '@/lib/speichern'
import type { ErpEinstellungenProps } from '../registry'

/** Schlüssel aus der Bezeichnung — fest, sobald er einmal vergeben ist (Zeilen speichern ihn). */
const schluesselAus = (label: string, vorhanden: string[]) => {
  const basis = label.normalize('NFKD').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '').toUpperCase() || 'ZUSCHLAG'
  let k = basis
  for (let i = 2; vorhanden.includes(k); i++) k = `${basis}_${i}`
  return k
}

/**
 * AP-27 8., 9. — **Einstellungen des Regieberichts**: zugelassene Zeilenarten und die Zuschlagsliste
 * (Bezeichnung, Prozent, aktiv). Gespeichert wird die Liste als Ganzes beim Verlassen eines Feldes; ein
 * Zuschlag wird nie gelöscht, nur abgeschaltet — Zeilen tragen seinen Schlüssel.
 */
export function RegieEinstellungen({ extra, speichere }: ErpEinstellungenProps) {
  const { t } = useTranslation()
  const [stand, setStand] = useState<RegieExtra>(() => regieExtra(extra))

  const schreibe = (neu: RegieExtra) => {
    setStand(neu)
    void speichere(neu as unknown as Record<string, unknown>)
  }
  const aendere = (i: number, teil: Partial<RegieZuschlag>, sofort: boolean) => {
    const zuschlaege = stand.zuschlaege.map((z, j) => (j === i ? { ...z, ...teil } : z))
    if (sofort) schreibe({ ...stand, zuschlaege })
    else setStand({ ...stand, zuschlaege })
  }

  return (
    <div className="space-y-3 border-t pt-3">
      <fieldset className="space-y-1 text-sm">
        <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.regie.lineKinds')}</legend>
        <div className="flex flex-wrap gap-4">
          {REGIE_LINE_KINDS.map((k) => (
            <label key={k} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={stand.zeilenarten.includes(k)}
                onChange={() => {
                  const neu = stand.zeilenarten.includes(k) ? stand.zeilenarten.filter((x) => x !== k) : [...stand.zeilenarten, k]
                  if (neu.length) schreibe({ ...stand, zeilenarten: REGIE_LINE_KINDS.filter((x) => neu.includes(x)) })
                }}
              />
              {t(`siteDocs.regie.groups.${k}`)}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.regie.surcharges')}</p>
        <p className="text-xs text-muted-foreground">{t('siteDocs.regie.surchargesHelp')}</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="py-1 pr-2">{t('siteDocs.regie.surchargeLabel')}</th>
              <th className="w-28 py-1 pr-2">{t('siteDocs.regie.surchargePercent')}</th>
              <th className="w-20 py-1">{t('siteDocs.regie.surchargeActive')}</th>
            </tr>
          </thead>
          <tbody>
            {stand.zuschlaege.map((z, i) => (
              <tr key={z.key}>
                <td className="py-1 pr-2">
                  <input className={feldKlasse} value={z.label} aria-label={t('siteDocs.regie.surchargeLabel')} onChange={(e) => aendere(i, { label: e.target.value }, false)} onBlur={() => schreibe(stand)} />
                </td>
                <td className="py-1 pr-2">
                  <input
                    className={`${feldKlasse} text-right`}
                    inputMode="decimal"
                    value={String(z.percent)}
                    aria-label={t('siteDocs.regie.surchargePercent')}
                    onChange={(e) => aendere(i, { percent: Number(e.target.value.replace(',', '.')) || 0 }, false)}
                    onBlur={() => schreibe(stand)}
                  />
                </td>
                <td className="py-1">
                  <input type="checkbox" checked={z.active} aria-label={t('siteDocs.regie.surchargeActive')} onChange={(e) => aendere(i, { active: e.target.checked }, true)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs"
          onClick={() => {
            const label = t('siteDocs.regie.newSurcharge')
            setStand({ ...stand, zuschlaege: [...stand.zuschlaege, { key: schluesselAus(label, stand.zuschlaege.map((z) => z.key)), label, percent: 0, active: true }] })
          }}
        >
          <Plus className="h-3.5 w-3.5" /> {t('siteDocs.regie.addSurcharge')}
        </button>
      </div>
    </div>
  )
}
